import { Alert, Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { DOCUMENT_LIBRARY_ACCEPT, titleFromDocumentFileName } from './documentLibraryAccept';
import DocumentUploadPendingForm from './DocumentUploadPendingForm';

const PRIMARY_BORDER = 'primary.main';

interface DocumentUploadZoneProps {
  uploading: boolean;
  uploadError: string | null;
  onDismissError: () => void;
  onUpload: (file: File, title: string, description: string) => void;
}

/** Drop or click to add a document to the library. */
export default function DocumentUploadZone({
  uploading,
  uploadError,
  onDismissError,
  onUpload,
}: DocumentUploadZoneProps) {
  const [title, setTitle] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: DOCUMENT_LIBRARY_ACCEPT,
    multiple: false,
    disabled: uploading,
    onDrop: (accepted) => {
      const file = accepted[0];
      if (!file) return;
      setTitle(titleFromDocumentFileName(file.name));
      setPendingFile(file);
    },
  });

  if (pendingFile) {
    return (
      <DocumentUploadPendingForm
        file={pendingFile}
        title={title}
        uploading={uploading}
        uploadError={uploadError}
        onTitleChange={setTitle}
        onUpload={() => {
          if (!title.trim()) return;
          onUpload(pendingFile, title.trim(), '');
          setPendingFile(null);
          setTitle('');
        }}
        onClear={() => {
          setPendingFile(null);
          setTitle('');
        }}
        onDismissError={onDismissError}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? PRIMARY_BORDER : 'divider',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          bgcolor: isDragActive ? 'action.selected' : 'action.hover',
          color: isDragActive ? PRIMARY_BORDER : 'text.secondary',
          transition: 'border-color 0.12s ease, background-color 0.12s ease',
          '&:hover': uploading ? undefined : { borderColor: PRIMARY_BORDER },
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="body2">
          {isDragActive ? 'Drop document here…' : 'Drag & drop a document, or click to choose'}
        </Typography>
      </Box>
      {uploadError && (
        <Alert severity="error" onClose={onDismissError} sx={{ py: 0 }}>
          {uploadError}
        </Alert>
      )}
    </Box>
  );
}
